import { useMemo, useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Lightweight client-side pagination hook + UI control for SGM México admin tables.
 * Usage:
 *   const { pageItems, controls } = usePagination(items, 10);
 *   {pageItems.map(...)}
 *   {controls}
 */
export function usePagination(items, pageSize = 10, testIdPrefix = "pager") {
  const [page, setPage] = useState(1);
  const total = items?.length || 0;
  const pages = Math.max(1, Math.ceil(total / pageSize));

  // Reset to page 1 if the list shrinks below current page
  useEffect(() => {
    if (page > pages) setPage(1);
  }, [pages, page]);

  const pageItems = useMemo(() => {
    if (!items) return [];
    const start = (page - 1) * pageSize;
    return items.slice(start, start + pageSize);
  }, [items, page, pageSize]);

  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  const controls = total > pageSize ? (
    <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-[#2E2E33] bg-[#1A1A1F]" data-testid={`${testIdPrefix}-controls`}>
      <div className="font-mono text-[10px] uppercase tracking-widest text-[#71717A]" data-testid={`${testIdPrefix}-range`}>
        // {from}–{to} de {total}
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page === 1}
          className="border border-[#2E2E33] hover:border-[#F59E0B] hover:text-[#F59E0B] p-1.5 disabled:opacity-30 disabled:hover:border-[#2E2E33] disabled:hover:text-current"
          data-testid={`${testIdPrefix}-prev`}
          aria-label="Anterior"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
        </button>
        <span className="font-mono text-[11px] text-[#D4D4D8] tabular-nums" data-testid={`${testIdPrefix}-page`}>
          {page} / {pages}
        </span>
        <button
          type="button"
          onClick={() => setPage((p) => Math.min(pages, p + 1))}
          disabled={page >= pages}
          className="border border-[#2E2E33] hover:border-[#F59E0B] hover:text-[#F59E0B] p-1.5 disabled:opacity-30 disabled:hover:border-[#2E2E33] disabled:hover:text-current"
          data-testid={`${testIdPrefix}-next`}
          aria-label="Siguiente"
        >
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  ) : null;

  return { pageItems, controls, page, setPage, pages, total };
}
